import { errorReporter } from '@/services/errorReportingService';
import type {
  DesktopStatus as ApiDesktopStatus,
  DesktopCheckCreated,
  DesktopCheckState as ApiDesktopCheckState,
} from '@/services/desktopConnectionApi/desktopConnectionApiSchemas';

/**
 * `capabilities` is additive and optional: desktop v1.2.48 and older answer the
 * loopback probe with no such key, which reads as "cannot do any capability-gated
 * feature" — never an error, and never a reason to reject the status. Unknown
 * strings are kept verbatim so a newer desktop can advertise capabilities this
 * build has never heard of.
 */
export type DesktopStatus = ApiDesktopStatus & {
  app: 'notego-desktop';
  protocolVersion: 1;
  capabilities?: string[];
};
export type DesktopCheck = DesktopCheckCreated;
export type DesktopCheckState = Omit<ApiDesktopCheckState, 'result'> & { result?: DesktopStatus | null };
/** The three deployments a desktop deep link may name. */
export type DesktopEnvironment = 'production' | 'staging' | 'local';

const API_URL = import.meta.env.VITE_API_URL;
export const DESKTOP_STATUS_URL = 'http://127.0.0.1:47832/v1/status';

/**
 * Last time this browser got any loopback response, as an ISO-8601 UTC string.
 * Written from `probeDesktop` — the single place a probe succeeds — so every
 * probe in the app records the sighting without each caller remembering to.
 */
export const DESKTOP_HANDOFF_SEEN_AT_KEY = 'notego.desktopHandoff.seenAt';

/**
 * JSON array of the `capabilities` that same sighting advertised, written
 * alongside `seenAt`. Lets a capability-gated link use the remembered path only
 * when the app last seen here could actually route it.
 */
export const DESKTOP_HANDOFF_CAPABILITIES_KEY = 'notego.desktopHandoff.capabilities';

/** Millisecond timestamp of the last loopback response, or null when unrecorded. */
export function readDesktopSeenAt(): number | null {
  try {
    const raw = localStorage.getItem(DESKTOP_HANDOFF_SEEN_AT_KEY);
    if (!raw) return null;
    const parsed = Date.parse(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    // Storage can be unavailable (private mode, blocked site data): no sighting.
    return null;
  }
}

/** Capabilities of the last sighting; empty when unrecorded, so nothing capability-gated is assumed. */
export function readDesktopSeenCapabilities(): string[] {
  try {
    const raw = localStorage.getItem(DESKTOP_HANDOFF_CAPABILITIES_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return areCapabilities(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function recordDesktopSeen(status: DesktopStatus): void {
  try {
    localStorage.setItem(DESKTOP_HANDOFF_SEEN_AT_KEY, new Date().toISOString());
    localStorage.setItem(DESKTOP_HANDOFF_CAPABILITIES_KEY, JSON.stringify(status.capabilities ?? []));
  } catch {
    // A browser that refuses storage still gets the live path; only the
    // remembered path is lost, which is the safe direction to fail.
  }
}

/** Every entry must be a string; the values themselves are deliberately not validated. */
function areCapabilities(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

export function isDesktopStatus(value: unknown): value is DesktopStatus {
  if (!value || typeof value !== 'object') return false;
  const data = value as Record<string, unknown>;
  return (
    data.app === 'notego-desktop' &&
    data.protocolVersion === 1 &&
    typeof data.version === 'string' &&
    data.version.length <= 64 &&
    /^\d+(\.\d+){0,3}(-[a-zA-Z0-9.-]+)?$/.test(data.version) &&
    ['darwin', 'win32', 'linux'].includes(String(data.platform)) &&
    ['arm64', 'x64', 'ia32', 'unknown'].includes(String(data.appArch)) &&
    (data.capabilities === undefined || areCapabilities(data.capabilities))
  );
}

/**
 * Chrome's Local Network Access permission for reaching 127.0.0.1 from a public
 * origin, or null when this browser has no such permission to query. Newer
 * Chrome splits loopback out as `loopback-network`; older LNA builds only know
 * `local-network-access`.
 */
export async function loopbackPermission(): Promise<PermissionState | null> {
  for (const name of ['loopback-network', 'local-network-access']) {
    try {
      const status = await navigator.permissions.query({ name } as unknown as PermissionDescriptor);
      return status.state;
    } catch {
      // Unknown permission name in this browser: try the next, then give up.
    }
  }
  return null;
}

export async function probeDesktop(signal: AbortSignal): Promise<DesktopStatus | null> {
  // Denied is an unknown install, never "not installed" — and no request can
  // succeed, so do not make one.
  const permission = await loopbackPermission();
  if (permission === 'denied' || signal.aborted) return null;
  const requestId = crypto.randomUUID().replaceAll('-', '');
  const timeout = new AbortController();
  const abort = () => timeout.abort();
  signal.addEventListener('abort', abort, { once: true });
  if (signal.aborted) timeout.abort();
  // While Chrome's permission prompt is up the request stays pending until the
  // person answers it. A short cap aborts it first, so the probe could never
  // succeed and handoff never fired. Leave it bounded only by the caller's signal.
  const timer = permission === 'prompt' ? undefined : window.setTimeout(abort, 2500);
  try {
    // Loopback access may need browser permission. Denial is an unknown install,
    // and the app-opening check uses HTTPS instead of requiring that permission.
    // No `cache: 'no-store'`: Chrome turns it into Cache-Control/Pragma request
    // headers, the preflight then asks for them, and the desktop server allows
    // only X-NoteGo-Request, so every probe failed. The desktop already answers
    // with `Cache-Control: no-store`, so nothing is cached either way.
    const response = await fetch(DESKTOP_STATUS_URL, {
      headers: { 'X-NoteGo-Request': requestId },
      signal: timeout.signal,
      credentials: 'omit',
      redirect: 'error',
    });
    if (!response.ok) return null;
    const data: unknown = await response.json();
    if (!isDesktopStatus(data) || (data as unknown as { requestId: string }).requestId !== requestId) return null;
    // Only a status that echoes this request is trusted evidence, so only that
    // counts as a sighting. A response that fails the echo check is rejected
    // everywhere else and must not be remembered as one either.
    recordDesktopSeen(data);
    return data;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
    signal.removeEventListener('abort', abort);
  }
}

async function request(path: string, token: string, signal: AbortSignal, method = 'GET'): Promise<unknown> {
  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}` },
    signal,
    cache: 'no-store',
    redirect: 'error',
  });
  if (!response.ok) throw new Error('Desktop check unavailable');
  return response.json();
}

export async function createDesktopCheck(token: string, signal: AbortSignal): Promise<DesktopCheck> {
  const data = (await request('/users/me/desktop-checks', token, signal, 'POST')) as Partial<DesktopCheck> | null;
  if (
    !data ||
    typeof data.requestId !== 'string' ||
    !/^[a-f0-9]{32}$/.test(data.requestId) ||
    typeof data.completionToken !== 'string' ||
    !/^[A-Za-z0-9_-]{43}$/.test(data.completionToken) ||
    typeof data.expiresAt !== 'string' ||
    !Number.isFinite(Date.parse(data.expiresAt))
  ) {
    throw new Error('Invalid desktop check');
  }
  return data as DesktopCheck;
}

/**
 * Every API origin each deployment is known to build against: the Cloud Run
 * and Cloud Functions origins `VITE_API_URL` actually holds in the production
 * and staging builds — verified 2026-09-11 by reading the deployed bundles at
 * app.notego.ai and notegostaging.web.app, not from the `.env.*` files (the
 * hosting workflow injects the value from Secret Manager, which wins over
 * them). Never list `api.notego.ai` / `api-staging.notego.ai`: neither exists
 * in DNS (notego-crossreference CONTRACT.md Rule 14), and a table holding only
 * those names mapped every deployed build to `null`, so no
 * `notego-desktop://` link was ever built and handoff never fired anywhere.
 * This table must grow whenever the API moves.
 */
const API_ORIGINS: Record<DesktopEnvironment, readonly string[]> = {
  production: [
    'https://fastapi-entry-point-4slfwjwq6q-uc.a.run.app',
    'https://us-central1-notegonan.cloudfunctions.net',
  ],
  staging: ['https://us-central1-notegostaging.cloudfunctions.net'],
  local: ['http://localhost:8000', 'http://127.0.0.1:8000'],
};

/**
 * The deployment this build talks to, or null when the API origin is not a
 * known one. Every `notego-desktop://` link shares this mapping so a staging
 * web build can never point a production desktop app at the wrong data.
 */
export function desktopEnvironment(apiUrl = API_URL): DesktopEnvironment | null {
  let origin: string;
  try {
    origin = new URL(apiUrl).origin;
  } catch {
    return null;
  }
  const environments = Object.keys(API_ORIGINS) as DesktopEnvironment[];
  const environment = environments.find((candidate) => API_ORIGINS[candidate].includes(origin)) ?? null;
  if (!environment) reportUnknownOrigin(origin);
  return environment;
}

let unknownOriginReported = false;

/**
 * A `null` environment silently disables every desktop deep link, which is how
 * the feature shipped dead for a release: nothing said so. Report it once per
 * page load. The origin is a build constant, never user data.
 */
function reportUnknownOrigin(origin: string): void {
  if (unknownOriginReported) return;
  unknownOriginReported = true;
  errorReporter.expected('Desktop deep links disabled: API origin is not a known deployment', undefined, {
    origin,
  });
}

export function desktopCheckUrl(check: DesktopCheck, apiUrl = API_URL): string {
  const environment = desktopEnvironment(apiUrl);
  if (!environment) throw new Error('Unsupported desktop check environment');
  const query = new URLSearchParams({ requestId: check.requestId, code: check.completionToken, environment });
  return `notego-desktop://check-version?${query}`;
}

export async function readDesktopCheck(
  check: DesktopCheck,
  token: string,
  signal: AbortSignal,
): Promise<DesktopCheckState> {
  const data = (await request(
    `/users/me/desktop-checks/${check.requestId}`,
    token,
    signal,
  )) as Partial<DesktopCheckState> | null;
  if (
    !data ||
    data.requestId !== check.requestId ||
    !['pending', 'completed', 'expired'].includes(String(data.status)) ||
    (data.status === 'completed' &&
      (!isDesktopStatus(data.result) ||
        typeof data.checkedAt !== 'string' ||
        !Number.isFinite(Date.parse(data.checkedAt))))
  )
    throw new Error('Invalid desktop check result');
  return data as DesktopCheckState;
}
